/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { BiRightArrow, BiDownArrow } from 'react-icons/bi';
import AppContext from 'context/Context';
import { useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getFarmTableData } from 'api/endpoints/TerrestrialServices';
import useApi from 'hooks/useApi';
import { get } from 'lodash';
import { isEmpty } from 'lodash';
import ServerSideTableFooter from 'components/common/advance-table/ServerSideTableFooter';
import ServerSideTable from 'components/common/advance-table/ServerSideTable';
import ServerSideTableWrapper from 'components/common/advance-table/ServerSideTableWrapper';
import CustomSpinner from 'components/common/CustomSpinner';
import { getTenantFromToken } from 'utils/routeUtils';

const ProjectNameCell = ({ row, onEvaluateClick, onMonitoringClick }) => (
  <Dropdown drop="right">
    <Dropdown.Toggle
      disabled
      style={{ cursor: 'pointer', fontWeight: 500 }}
      as={'span'}
    >
      <span>{'row.project_name'}</span>
    </Dropdown.Toggle>

    <Dropdown.Menu>
      {/* <Dropdown.Item onClick={() => onEvaluateClick(row)}>
        Evaluation
      </Dropdown.Item> */}
      {row.monitoring_status === true && (
        <Dropdown.Item onClick={() => onMonitoringClick(row)}>
          Monitoring
        </Dropdown.Item>
      )}
    </Dropdown.Menu>
  </Dropdown>
);

const ForwardRefFontAwesomeIcon = React.forwardRef((props, ref) => (
  <FontAwesomeIcon {...props} forwardedRef={ref} className="my-2" />
));

const ActionCell = ({
  row,
  onViewClick,
  onUpdateClick,
  onDeleteClick,
  onStartProcessClick
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStartProcessModal, setShowStartProcessModal] = useState(false);

  const isEditable =
    row.status && row.status?.toLowerCase() !== 'Processing'.toLowerCase();
  const isProcessable =
    row.status && row.status?.toLowerCase() === 'New'.toLowerCase();

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    onDeleteClick(row);
    handleCloseDeleteModal();
  };

  const handleCloseStartProcessingModal = () => {
    setShowStartProcessModal(false);
  };

  const handleConfirmStartProcessing = () => {
    onStartProcessClick(row);
    handleCloseStartProcessingModal();
  };

  return (
    <>
      {isProcessable && (
        <Modal
          show={showStartProcessModal}
          onHide={handleCloseStartProcessingModal}
          centered
          className="p-5"
        >
          <Modal.Header closeButtonc className="border-0">
            <Modal.Title>Confirm Start Processing</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to start processing for {row.project_name}?
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="light" onClick={handleCloseStartProcessingModal}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleConfirmStartProcessing}>
              Start Processing
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

ActionCell.propTypes = {
  row: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onUpdateClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onStartProcessClick: PropTypes.func.isRequired
};

const FarmLevelReportTable = ({ showFilter, setExportData, setExportKey }) => {
  const tenant = getTenantFromToken();

  const getFarmTableDataAPI = useApi(getFarmTableData);

  const [tableData, setTableData] = useState(null);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [canNextPage, setCanNextPage] = useState(true);
  const [filters, setFilters] = useState({});
  const [cursor, setCursor] = useState('');

  useEffect(() => {
    let limit = pageSize;
    let cursor = pageIndex * pageSize;
    let filter = filters;
    setCursor(cursor);
    getFarmTableDataAPI.request({ cursor, limit, tenant, filter });
  }, [pageIndex, filters, pageSize]);

  useEffect(() => {
    const tabledata = get(getFarmTableDataAPI, 'data.results', []);
    // const exportKey = get(getFarmTableDataAPI, 'data.results.export_key', '');
    setExportKey(get(getFarmTableDataAPI, 'data.results.export_key', ''));
    // setLoading(getFarmTableDataAPI.loading);
    setRowCount(get(getFarmTableDataAPI, 'data.results.total', 0));
    setCanNextPage(tabledata.remaining_records > 0);
    setTableData(tabledata.data);
    setExportData(tabledata.data);
  }, [getFarmTableDataAPI.data]);

  const navigate = useNavigate();

  const handleNavigate = (tenant, page, projectNameSnakeCase) => {
    // const projectNameSnakeCase = projectName.toLowerCase().replace(/\s+/g, '_');
    navigate(`/${tenant}/${page}/${projectNameSnakeCase}`);
  };

  const flattenedData = tableData
    ? Object.keys(tableData)?.map((key, index) => {
        const report = tableData[key].farmland_report;

        const isEmptyReport = Object.values(report).every(
          item =>
            item === null || (item?.value === null && item?.color === null)
        );

        if (isEmptyReport) {
          return {
            s_no: index + 1, // Add serial number starting from 1
            id: key,
            ...Object.keys(report).reduce((acc, curr) => {
              if (curr !== 'farm_id') {
                acc[curr] = ''; // Set all values to empty strings
              }
              return acc;
            }, {})
          };
        }

        return {
          s_no: index + 1, // Add serial number starting from 1
          id: key,
          ...Object.keys(report).reduce((acc, curr) => {
            if (curr !== 'farm_id') {
              acc[curr] = report[curr]?.value || report[curr] || '';
              if (report[curr]?.color) {
                acc[`${curr}_color`] = report[curr]?.color;
              }
            }
            return acc;
          }, {})
        };
      })
    : []; // Provide a default empty array if tableData is null or empty

  // Ensure columns are only defined if flattenedData is not empty
  const columns =
    flattenedData.length > 0
      ? [
          ...Object.keys(flattenedData[0])
            .filter(
              key =>
                !key.includes('_color') && key !== 'farm_id' && key !== 'id'
            )
            .map((key, index) => ({
              Header: key
                .replace(/_/g, ' ')
                .replace(/(^\w|\b\w(?![^()]*\)))/g, c => c.toUpperCase()), // Capitalizes first letter of each word
              accessor: key,
              isSticky: index === 1 || index === 2,
              Cell: ({ value, row }) => {
                const colorKey = `${key}_color`; // Define the corresponding color key

                const displayValue =
                  typeof value === 'object' && value !== null ? ' - ' : value;

                if (key === 'farm_name') {
                  return (
                    <span
                      style={{
                        color: 'blue',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                      onClick={() =>
                        handleNavigate(tenant, 'monitor', row.original.id)
                      }
                    >
                      {displayValue || ''}
                    </span>
                  );
                }
                if (
                  typeof value === 'string' &&
                  value.includes('<a') &&
                  value.includes('href=')
                ) {
                  const match = value.match(/href="([^"]+)"/);
                  const href = match?.[1];
                  if (href) {
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: row.original[colorKey],
                          textDecoration: 'underline'
                        }}
                      >
                        View Map
                      </a>
                    );
                  }
                }

                return (
                  <span style={{ color: row.original[colorKey] || 'inherit' }}>
                    {displayValue || ''}
                  </span>
                );
              }
            }))
        ]
      : []; // Empty columns if flattenedData is empty

  const handlePageChange = newPageIndex => {
    setPageIndex(newPageIndex);
  };

  const handlePageSizeChange = newPageSize => {
    setPageSize(newPageSize);
    setPageIndex(0); // Reset to first page on page size change
    setTableData(null); // Clear the current table data to force re-render
  };

  const handleFilterChange = (columnId, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [columnId]: value
    }));
  };

  return (
    <ServerSideTableWrapper
      key={`${pageSize}-${pageIndex}`}
      columns={columns}
      data={flattenedData}
      pagination
      sortable
      perPage={pageSize}
      className="min-vh-50 py-5"
      stickyColumns={[]}
    >
      {getFarmTableDataAPI.loading && !showFilter ? (
        <CustomSpinner />
      ) : (
        <ServerSideTable
          key={`${pageSize}-${pageIndex}`}
          table
          headerClassName="bg-200 text-900 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: 'fs--1 mb-0'
          }}
          filters={filters}
          handleFilterChange={handleFilterChange}
          showFilterRow={showFilter}
          pageIndex={pageIndex}
          pageSize={pageSize}
          cursor={cursor}
        />
      )}
      <div className="mt-3">
        <ServerSideTableFooter
          rowCount={rowCount} // Total number of rows from the API
          pageSize={pageSize}
          pageIndex={pageIndex}
          setPageSize={handlePageSizeChange}
          canPreviousPage={pageIndex > 0}
          canNextPage={canNextPage}
          nextPage={() => handlePageChange(pageIndex + 1)}
          previousPage={() => handlePageChange(Math.max(0, pageIndex - 1))}
          rowsPerPageSelection
          navButtons
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          // rowsPerPageOptions={[5, 10, 20, 25]}
          // rowInfo
        />
      </div>
    </ServerSideTableWrapper>
  );
};

FarmLevelReportTable.propTypes = {
  data: PropTypes.array.isRequired,
  deletePorjectAPI: PropTypes.object.isRequired,
  startProcessingAPI: PropTypes.object.isRequired
};

ServerSideTableWrapper.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      Cell: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
          value: PropTypes.func.isRequired,
          row: PropTypes.shape({
            original: PropTypes.object.isRequired
          }).isRequired
        })
      ])
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  pagination: PropTypes.bool,
  perPage: PropTypes.number
};

export default FarmLevelReportTable;
